import { Search } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  CoverageMentionSchema,
  CoverageSchema,
} from '@/services/types/coverage-types';

import { MentionsFilters } from './MentionsFilters';
import { MentionsTable } from './MentionsTable';

interface MentionsListProps {
  mentions: CoverageMentionSchema[];
  onMentionSelect: (mentionId: string) => void;
  availableCoverages?: CoverageSchema[];
}

export const MentionsList = ({
  mentions,
  onMentionSelect,
  availableCoverages = [],
}: MentionsListProps) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [artifactTypeFilter, setArtifactTypeFilter] = useState<string>('all');
  const [coverageFilter, setCoverageFilter] = useState<string>('all');
  const [validOnlyFilter, setValidOnlyFilter] = useState(false);
  const [sortField, setSortField] = useState<string>('id');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Filter mentions based on current filters
  const filteredMentions = mentions.filter((mention) => {
    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch =
        mention.id.toLowerCase().includes(searchLower) ||
        mention.artifact_id.toLowerCase().includes(searchLower) ||
        mention.extraction?.carrier?.name
          ?.toLowerCase()
          .includes(searchLower) ||
        mention.extraction?.policy_type?.toLowerCase().includes(searchLower) ||
        mention.extraction?.policy_identifiers?.some(
          (identifier) =>
            identifier.identifier_value.toLowerCase().includes(searchLower) ||
            identifier.identifier_type.toLowerCase().includes(searchLower),
        ) ||
        false;
      if (!matchesSearch) return false;
    }

    // Status filter
    if (statusFilter !== 'all' && mention.status !== statusFilter) {
      return false;
    }

    // Category filter
    if (
      categoryFilter !== 'all' &&
      mention.extraction?.category !== categoryFilter
    ) {
      return false;
    }

    // Artifact type filter
    if (
      artifactTypeFilter !== 'all' &&
      mention.artifact_type !== artifactTypeFilter
    ) {
      return false;
    }

    // Coverage filter
    if (coverageFilter !== 'all') {
      if (coverageFilter === 'unlinked') {
        if (mention.coverage_id !== null) return false;
      } else if (coverageFilter === 'linked') {
        if (mention.coverage_id === null) return false;
      } else if (mention.coverage_id !== coverageFilter) {
        // Specific coverage ID
        return false;
      }
    }

    // Valid only filter
    if (validOnlyFilter && !mention.validation?.is_valid_coverage_mention) {
      return false;
    }

    return true;
  });

  // Sort mentions
  const sortedMentions = [...filteredMentions].sort((a, b) => {
    let aValue: any;
    let bValue: any;

    switch (sortField) {
      case 'id':
        aValue = a.id;
        bValue = b.id;
        break;
      case 'status':
        aValue = a.status || '';
        bValue = b.status || '';
        break;
      case 'category':
        aValue = a.extraction?.category || '';
        bValue = b.extraction?.category || '';
        break;
      case 'confidence':
        aValue = a.extraction?.confidence || 0;
        bValue = b.extraction?.confidence || 0;
        break;
      case 'specificity':
        aValue = a.validation?.coverage_specificity_score || 0;
        bValue = b.validation?.coverage_specificity_score || 0;
        break;
      case 'carrier':
        aValue = a.extraction?.carrier?.name || '';
        bValue = b.extraction?.carrier?.name || '';
        break;
      case 'created_at':
        aValue = (a as any).created_at || '';
        bValue = (b as any).created_at || '';
        break;
      default:
        aValue = a.id;
        bValue = b.id;
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection(field === 'created_at' ? 'desc' : 'desc');
    }
  };

  const getDisplayText = (mention: CoverageMentionSchema): string => {
    const parts = [];

    if (mention.extraction?.category) {
      parts.push(mention.extraction.category.replace('_', ' '));
    }

    if (mention.extraction?.carrier?.name) {
      parts.push(mention.extraction.carrier.name);
    } else if (mention.extraction?.policy_type) {
      parts.push(mention.extraction.policy_type);
    } else if (
      mention.extraction?.policy_identifiers &&
      mention.extraction.policy_identifiers.length > 0
    ) {
      const primaryId = mention.extraction.policy_identifiers[0];
      parts.push(
        `${primaryId.identifier_type}: ${primaryId.identifier_value.slice(0, 15)}...`,
      );
    }

    return parts.length > 0 ? parts.join(' • ') : 'Coverage mention';
  };

  return (
    <div className="space-y-3">
      {/* Search and Filters */}
      <div className="flex flex-col gap-3">
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search mentions by ID, artifact, carrier, policy..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-9"
            />
          </div>
        </div>

        <MentionsFilters
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          categoryFilter={categoryFilter}
          onCategoryFilterChange={setCategoryFilter}
          artifactTypeFilter={artifactTypeFilter}
          onArtifactTypeFilterChange={setArtifactTypeFilter}
          coverageFilter={coverageFilter}
          onCoverageFilterChange={setCoverageFilter}
          availableCoverages={availableCoverages}
          validOnlyFilter={validOnlyFilter}
          onValidOnlyFilterChange={setValidOnlyFilter}
        />
      </div>

      {/* Results count */}
      <div className="flex items-center justify-between text-sm text-gray-600">
        <span>
          {sortedMentions.length} of {mentions.length} mentions
        </span>
        {searchTerm && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSearchTerm('')}
            className="h-7"
          >
            Clear search
          </Button>
        )}
      </div>

      {/* Table */}
      <MentionsTable
        mentions={sortedMentions}
        onMentionSelect={onMentionSelect}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSort}
        availableCoverages={availableCoverages}
      />
    </div>
  );
};
